// Wykres "Kiedy najtaniej do X" — średnia cena per miesiąc kalendarzowy
// z ostatnich 12 miesięcy `route_price_history`. Bar chart z recharts.
// Lazy-loaded przez React Query — nie blokuje SSR landingu.

import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Calendar, TrendingDown } from "lucide-react";
import { getMonthlyPrices, type MonthlyPricePoint } from "@/lib/monthly-prices.functions";

function fmtPln(n: number) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    maximumFractionDigits: 0,
  }).format(n);
}

export function MonthlyPriceChart({
  from,
  to,
  fromName,
  toName,
  className,
}: {
  from: string;
  to: string;
  fromName?: string;
  toName?: string;
  className?: string;
}) {
  const fetchMonthly = useServerFn(getMonthlyPrices);
  const { data, isLoading } = useQuery<MonthlyPricePoint[]>({
    queryKey: ["monthly-prices", from, to],
    queryFn: () => fetchMonthly({ data: { from, to } }),
    staleTime: 60 * 60 * 1000, // 1h — agregaty miesięczne zmieniają się powoli
  });

  const { points, cheapest, expensive } = useMemo(() => {
    const pts = data ?? [];
    if (pts.length === 0) return { points: pts, cheapest: null, expensive: null };
    const sorted = [...pts].sort((a, b) => a.avg - b.avg);
    return { points: pts, cheapest: sorted[0], expensive: sorted[sorted.length - 1] };
  }, [data]);

  // Brak danych = nie renderujemy nic (zero loading state w SSR).
  // To karta "extra value" — nie chcemy pokazywać pustego placeholdera.
  if (!isLoading && points.length < 2) return null;

  return (
    <section className={`glass mt-6 rounded-2xl p-5 ${className ?? ""}`}>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-xl font-bold">
            <Calendar className="h-5 w-5 text-primary" aria-hidden="true" />
            Kiedy najtaniej {fromName && toName ? `z ${fromName} do ${toName}` : "lecieć tą trasą"}?
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Średnia cena „od" wg miesiąca (ostatnie 12 miesięcy z naszej historii).
          </p>
        </div>
        {cheapest && (
          <div className="hidden sm:block shrink-0 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-right ring-1 ring-emerald-500/30">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Najtaniej
            </div>
            <div className="flex items-center gap-1 font-mono text-sm font-bold text-emerald-700 dark:text-emerald-300">
              <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
              {cheapest.label} · {fmtPln(cheapest.avg)}
            </div>
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-lg bg-muted/40" aria-hidden="true" />
      ) : (
        <>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                <XAxis
                  dataKey="label"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v} zł`}
                  width={56}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string) => [
                    fmtPln(value),
                    name === "avg" ? "Średnio" : name === "min" ? "Min" : name,
                  ]}
                  labelFormatter={(label: string, items) => {
                    const p = items?.[0]?.payload as MonthlyPricePoint | undefined;
                    return p ? `${label} · ${p.samples} dni z danymi` : label;
                  }}
                />
                <Bar dataKey="avg" radius={[6, 6, 0, 0]}>
                  {points.map((p) => (
                    <Cell
                      key={p.month}
                      fill={
                        p.month === cheapest?.month
                          ? "hsl(142 76% 45%)"
                          : p.month === expensive?.month
                            ? "hsl(25 95% 60%)"
                            : "hsl(var(--primary) / 0.7)"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {cheapest && expensive && cheapest.month !== expensive.month && (
            <p className="mt-3 text-xs text-muted-foreground">
              Różnica między najtańszym ({cheapest.label}: {fmtPln(cheapest.avg)})
              a najdroższym ({expensive.label}: {fmtPln(expensive.avg)}) miesiącem to{" "}
              <span className="font-semibold text-foreground">
                {Math.round(((expensive.avg - cheapest.avg) / cheapest.avg) * 100)}%
              </span>
              .
            </p>
          )}
        </>
      )}
    </section>
  );
}
